import { formatDate, formatPrice } from "@luce/core";

import {
  SubscriptionCancellationReasonEnum,
  SubscriptionStatusEnum,
} from "../__generated__/graphql";
import { CAMPAIGN_ID_PARAM } from "./constant";
import type {
  ClientSubscription,
  ClientSubscriptionStatus,
} from "./gqlhooks/use-client-subscription";

export function withCampaignId(path: string, campaignId?: string): string {
  if (!campaignId) return path;

  const [base, search] = path.split("?");
  const params = new URLSearchParams(search);
  if (params.has(CAMPAIGN_ID_PARAM)) return path;

  params.set(CAMPAIGN_ID_PARAM, campaignId);
  return `${base}?${params.toString()}`;
}

export function formatPlusDate(date?: string | null): string | null {
  if (!date) return null;
  return formatDate(new Date(date), "d MMM yyyy");
}

export function formatPlusMonth(date?: string | null): string | null {
  if (!date) return null;
  return formatDate(new Date(date), "MMM yyyy");
}

export type PlusStatusTone = "success" | "warning" | "danger" | "neutral";

export interface PlusStatusView {
  label: string;
  tone: PlusStatusTone;
  subscriptionEnded: boolean;
  renewalLabel: string | null;
  renewalTone: PlusStatusTone;
  renewalDate: string | null;
  autoRenewOff: boolean;
  canCancel: boolean;
  canResubscribe: boolean;
  resubscribeLabel: string | null;
  paymentPending: boolean;
}

export function getPlusStatusView(
  status: ClientSubscriptionStatus,
  renewalDate?: string | null,
): PlusStatusView {
  const date = formatPlusDate(renewalDate);

  switch (status) {
    case SubscriptionStatusEnum.Active:
      return {
        label: "Active",
        tone: "success",
        subscriptionEnded: false,
        renewalLabel: date ? `Renews on ${date}` : null,
        renewalTone: "neutral",
        renewalDate: date,
        autoRenewOff: false,
        canCancel: true,
        canResubscribe: false,
        resubscribeLabel: null,
        paymentPending: false,
      };
    case SubscriptionStatusEnum.Cancelled:
      return {
        label: "Cancelled",
        tone: "danger",
        subscriptionEnded: false,
        renewalLabel: date ? `end on ${date}` : null,
        renewalTone: "danger",
        renewalDate: date,
        autoRenewOff: true,
        canCancel: false,
        canResubscribe: true,
        resubscribeLabel: "Resume subscription",
        paymentPending: false,
      };
    case SubscriptionStatusEnum.PendingPayment:
      return {
        label: "Payment Required",
        tone: "danger",
        subscriptionEnded: false,
        // The failure banner carries the message; a due date next to the price
        // would read as a normal upcoming renewal.
        renewalLabel: null,
        renewalTone: "neutral",
        renewalDate: date,
        autoRenewOff: false,
        canCancel: false,
        canResubscribe: false,
        resubscribeLabel: null,
        paymentPending: true,
      };
    case SubscriptionStatusEnum.Inactive:
    default:
      return {
        label: "Inactive",
        tone: "neutral",
        subscriptionEnded: true,
        renewalLabel: null,
        renewalTone: "neutral",
        renewalDate: null,
        autoRenewOff: false,
        canCancel: false,
        canResubscribe: true,
        // No inline CTA: an ended plan resubscribes through the order card.
        resubscribeLabel: null,
        paymentPending: false,
      };
  }
}

export interface PlusSavingsView {
  total: string;
  average: string;
  monthsActive: number;
  memberSince: string | null;
}

// Savings are history, not a benefit: they stay on the card once the plan ends.
export function getPlusSavingsView(
  subscription: ClientSubscription | null,
): PlusSavingsView | null {
  if (!subscription || subscription.totalMonthsActive <= 0) return null;

  return {
    total: formatPrice(subscription.totalSavings),
    average: formatPrice(subscription.averageSavingsPerMonth),
    monthsActive: subscription.totalMonthsActive,
    memberSince: formatPlusMonth(subscription.firstSubscribedAt),
  };
}

export const CANCEL_REASON_OPTIONS: {
  value: SubscriptionCancellationReasonEnum;
  label: string;
}[] = [
  { value: SubscriptionCancellationReasonEnum.TooExpensive, label: "Too expensive" },
  { value: SubscriptionCancellationReasonEnum.NotUsingEnough, label: "Not using it enough" },
  { value: SubscriptionCancellationReasonEnum.MissingFeatures, label: "Missing features I need" },
  {
    value: SubscriptionCancellationReasonEnum.BetterAlternative,
    label: "Found a better alternative",
  },
  { value: SubscriptionCancellationReasonEnum.TechnicalIssues, label: "Technical issues" },
  { value: SubscriptionCancellationReasonEnum.TakingABreak, label: "Just taking a break" },
  { value: SubscriptionCancellationReasonEnum.Other, label: "Other" },
];
